import { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Progress from '@/components/ui/Progress';
import Spinner from '@/components/ui/Spinner';
import clsx from 'clsx';
import { API_URL, ttsJobApi, getErrorMessage } from '@/lib/api';
import { JobControlButtons, PauseStatusIndicator, CheckpointPreview } from '@/components/tts';
import { ToastContainer, type ToastProps } from '@/components/ui/Toast';
import { getDisplayTitleFromPdfPath, type TTSJobStatus, type CheckpointId } from '@/types/api';

// Types matching the API responses
interface JobData {
  id?: number;
  job_id: string;
  status: TTSJobStatus;
  progress?: number;
  status_message?: string;
  created_at?: string | number;
  updated_at?: string | number;
  finished_at?: string | number;
  error_message?: string | null;
  metadata?: string;
  voice_id?: string;
  pdf_path?: string;
  tenant_id?: string;
  // Pause/Stop fields
  pause_requested?: boolean;
  stop_requested?: boolean;
  paused_at_checkpoint?: CheckpointId | null;
  paused_at?: string | null;
  stopped_at?: string | null;
  stop_reason?: string | null;
}

interface ParsedMetadata {
  job_id?: string;
  pdf_path?: string;
  tenant_id?: string;
  voice_id?: string;
  chunk_size?: number;
  quality?: string;
  skip_credits?: boolean;
  dry_run?: boolean;
  isbn?: string;
}

interface StepDetail {
  started: boolean;
  completed: boolean;
  startTime: string | null;
  endTime: string | null;
}

interface JobStatusResponse {
  success: boolean;
  job_id: string;
  status: string;
  last_event: string | null;
  current_step: string | null;
  last_completed_step: string | null;
  next_step: string | null;
  is_stuck: boolean;
  stuck_reason: string | null;
  events_count: number;
  last_activity: string | null;
  suggested_retry_step: string | null;
  pipeline_progress: {
    completed_steps: number;
    total_steps: number;
    percentage: number;
    step_details: Record<string, StepDetail>;
  };
  timestamp: string;
}

interface BatchData {
  batches?: Array<{
    batch_id: string;
    status: string;
    created_at?: string;
    completed_at?: string;
    items_count?: number;
  }>;
  total?: number;
  completed?: number;
  pending?: number;
  failed?: number;
}

interface TelemetryEvent {
  event_name: string;
  timestamp: string;
  data?: Record<string, unknown>;
}

// Voice ID to name mapping
const VOICE_MAP: Record<string, string> = {
  'RFBMQGMs3dbpbuSvswMJ': 'Catherine Brunet',
  '2xgQghgxwdXTjTfiIS15': 'Gui Nadon',
  '2ASpqz3QzBrKfCGbLCBu': 'Paul Doucet',
  'UsvCCW6EWg7q8TOzhJA9': 'Nicolas Charbonneaux',
  'WDMQvMKpQdH4A2KBHxxh': 'Ingrid Falaise',
  '4dT4fLJG2XWMXWcow22v': 'Frédérique Dufort',
  'jKOLWvk2onl8Ek84PcOI': 'Patrice Robitaille',
  '05uD6t03H90aDCxYSy88': 'Fanny Mallette',
  '6u6JbqKdaQy89ENzLSju': 'Brielle',
  'gBqlM3o8u9wre6LWb5c3': 'Antonio Raluy Zierold',
  'haNSK4tJSgMmLnbneUXN': 'Lina Franco',
};

// Pipeline steps configuration
const PIPELINE_STEPS = [
  { key: 'pdf_ingestion', name: 'PDF Ingest', icon: '📄' },
  { key: 'image_processing', name: 'Image Processor', icon: '🖼️' },
  { key: 'extract_structure', name: 'Chapter Detector', icon: '📑' },
  { key: 'synthesize_tts', name: 'TTS Synthesis', icon: '🔊' },
  { key: 'assemble_audio', name: 'Audio Assembler', icon: '🎵' },
  { key: 'generate_credits', name: 'Credits Generator', icon: '📝' },
  { key: 'qa_finalize', name: 'QA Finalize', icon: '✅' },
];

function getStepStatusStyle(stepData: StepDetail | undefined) {
  if (!stepData) return 'bg-slate-100 border-slate-200 text-slate-500';
  if (stepData.completed) return 'bg-success-100 border-success-300 text-success-700';
  if (stepData.started) return 'bg-primary-100 border-primary-300 text-primary-700 animate-pulse';
  return 'bg-slate-100 border-slate-200 text-slate-500';
}

function formatDuration(ms: number | null): string {
  if (!ms) return '--';
  const seconds = Math.floor(ms / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${minutes}m ${remainingSeconds}s`;
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}h ${remainingMinutes}m`;
}

function calculateDuration(startTime: string | null, endTime: string | null): number | null {
  if (!startTime) return null;
  try {
    const end = endTime ? new Date(endTime).getTime() : Date.now();
    return end - new Date(startTime).getTime();
  } catch {
    return null;
  }
}

function formatTime(dateStr: string | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleTimeString();
  } catch {
    return '--';
  }
}

function minutesSince(dateStr: string | null): number | null {
  if (!dateStr) return null;
  try {
    const ms = Date.now() - new Date(dateStr).getTime();
    if (!Number.isFinite(ms) || ms < 0) return null;
    return Math.floor(ms / 60000);
  } catch {
    return null;
  }
}

function parseMetadata(metadataStr: string | undefined): ParsedMetadata {
  if (!metadataStr) return {};
  try {
    return JSON.parse(metadataStr);
  } catch {
    return {};
  }
}

export default function TTSJobMonitorPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [eventFilter, setEventFilter] = useState('all');
  
  // Data state
  const [job, setJob] = useState<JobData | null>(null);
  const [statusData, setStatusData] = useState<JobStatusResponse | null>(null);
  const [batchData, setBatchData] = useState<BatchData | null>(null);
  const [events, setEvents] = useState<TelemetryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  // Helper to add a toast notification
  const addToast = useCallback((title: string, type: ToastProps['type'], message?: string) => {
    const id = `toast-${Date.now()}`;
    setToasts(prev => [...prev, { id, type, title, message, onClose: removeToast }]);
  }, []);

  // Helper to remove a toast notification
  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // Handler for pause action
  const handlePause = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await ttsJobApi.pause(jobId);
      addToast('Pause Requested', 'success', response.message || 'Job will pause at next checkpoint');
      setJob(prev => prev ? { ...prev, pause_requested: true } : null);
    } catch (err) {
      addToast('Pause Failed', 'error', getErrorMessage(err));
    }
  }, [jobId, addToast]);

  // Handler for resume action
  const handleResume = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await ttsJobApi.resume(jobId);
      addToast('Job Resumed', 'success', response.message || 'Processing has resumed');
      setJob(prev => prev ? { 
        ...prev, 
        status: 'processing' as TTSJobStatus, 
        pause_requested: false, 
        paused_at_checkpoint: null,
        paused_at: null 
      } : null);
    } catch (err) {
      addToast('Resume Failed', 'error', getErrorMessage(err));
    }
  }, [jobId, addToast]);

  // Handler for stop action
  const handleStop = useCallback(async () => {
    if (!jobId) return;
    try {
      const response = await ttsJobApi.stop(jobId, 'User requested stop');
      addToast('Job Stopped', 'success', response.message || 'Job has been permanently stopped');
      setJob(prev => prev ? { 
        ...prev, 
        status: 'stopped' as TTSJobStatus, 
        stopped_at: new Date().toISOString() 
      } : null);
    } catch (err) {
      addToast('Stop Failed', 'error', getErrorMessage(err));
    }
  }, [jobId, addToast]);

  // Handler for delete action
  const handleDelete = useCallback(async () => {
    if (!jobId) return;
    try {
      await ttsJobApi.delete(jobId, true);
      addToast('Job Deleted', 'success', 'Job and files have been removed');
      setTimeout(() => navigate('/tts/jobs'), 1500);
    } catch (err) {
      addToast('Delete Failed', 'error', getErrorMessage(err));
    }
  }, [jobId, navigate, addToast]);

  const fetchData = useCallback(async () => {
    if (!jobId) return;

    try {
      const [jobResponse, statusResponse, batchResponse, telemetryResponse] = await Promise.all([
        fetch(`${API_URL}/tts/jobs/${jobId}`),
        fetch(`${API_URL}/tts/jobs/${jobId}/status`).catch(() => null),
        fetch(`${API_URL}/tts/jobs/${jobId}/batches`).catch(() => null),
        fetch(`${API_URL}/tts/telemetry/timeline/${jobId}`).catch(() => null),
      ]);

      if (!jobResponse.ok) {
        throw new Error(`Failed to fetch job: ${jobResponse.status}`);
      }

      const jobData = await jobResponse.json() as JobData;
      setJob(jobData);

      if (statusResponse?.ok) {
        const data = await statusResponse.json() as JobStatusResponse;
        setStatusData(data);
      }

      if (batchResponse?.ok) {
        const data = await batchResponse.json() as BatchData;
        setBatchData(data);
      }

      if (telemetryResponse?.ok) {
        const data = await telemetryResponse.json() as { events?: TelemetryEvent[] };
        if (data.events) {
          setEvents(data.events.slice(-50).reverse());
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching job data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load job data');
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh polling
  useEffect(() => {
    if (!autoRefresh || !jobId) return;
    
    // Stop polling if job is completed, failed, paused, or stopped
    if (job?.status === 'completed' || job?.status === 'failed' || job?.status === 'stopped') return;

    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, [autoRefresh, jobId, job?.status, fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="space-y-6">
        <Link
          to="/tts/jobs"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Jobs
        </Link>
        <Card>
          <CardBody className="text-center py-12">
            <ExclamationTriangleIcon className="w-12 h-12 text-danger-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Error Loading Job</h2>
            <p className="text-slate-500">{error || 'Job not found'}</p>
            <Button
              variant="primary"
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  // Parse job metadata and compute display values
  const metadata = parseMetadata(job.metadata);
  const title =
    getDisplayTitleFromPdfPath(metadata.pdf_path) ||
    getDisplayTitleFromPdfPath(job.pdf_path) ||
    'Unknown file';
  const voiceId = metadata.voice_id || job.voice_id || '';
  const voiceName = VOICE_MAP[voiceId] || voiceId || 'Unknown Voice';

  // Compute progress and stats
  const pipelineProgress = statusData?.pipeline_progress;
  const stepDetails = pipelineProgress?.step_details || {};
  const progress = pipelineProgress?.percentage || job.progress || 0;
  const currentStep = pipelineProgress?.completed_steps || 0;
  const totalSteps = pipelineProgress?.total_steps || 7;

  // Compute duration
  const startTime = job.created_at 
    ? (typeof job.created_at === 'number' 
        ? new Date(job.created_at < 10000000000 ? job.created_at * 1000 : job.created_at)
        : new Date(job.created_at))
    : null;
  const startTimeStr = startTime ? startTime.toLocaleTimeString() : '--';
  const totalDuration = startTime 
    ? formatDuration(Date.now() - startTime.getTime())
    : '--';

  // Batch stats
  const ttsBatches = {
    total: batchData?.total || 0,
    completed: batchData?.completed || 0,
    inProgress: (batchData?.total || 0) - (batchData?.completed || 0) - (batchData?.pending || 0) - (batchData?.failed || 0),
    pending: batchData?.pending || 0,
  };

  // Status badge
  const isRunning = (job.status === 'processing' || job.status === 'pending' || job.status === 'running') && !job.pause_requested;
  const statusVariant = job.status === 'completed' ? 'success' 
    : job.status === 'failed' ? 'danger'
    : job.status === 'paused' ? 'warning'
    : job.status === 'stopped' ? 'slate'
    : job.pause_requested ? 'warning'
    : 'primary';
  const statusLabel = job.pause_requested && isRunning ? 'Pausing...' 
    : job.status === 'processing' ? 'Processing'
    : job.status?.charAt(0).toUpperCase() + job.status?.slice(1);

  // Filter events
  const filteredEvents = events.filter(event => {
    if (eventFilter === 'all') return true;
    if (eventFilter === 'tts') return event.event_name.includes('tts') || event.event_name.includes('synthesize');
    if (eventFilter === 'success') return event.event_name.includes('_end') || event.event_name.includes('complete');
    return true;
  });

  /**
   * "Stuck" is often a false positive for large PDFs. We gate the warning behind a
   * longer inactivity threshold and use softer language.
   */
  const STUCK_WARNING_AFTER_MINUTES = 240; // 4 hours
  const lastActivityMinutes = minutesSince(statusData?.last_activity || null);
  const showSlowProcessingWarning =
    Boolean(statusData?.is_stuck) &&
    job.status !== 'completed' &&
    job.status !== 'failed' &&
    lastActivityMinutes !== null &&
    lastActivityMinutes >= STUCK_WARNING_AFTER_MINUTES;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to={`/tts/jobs/${jobId}`}
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Job
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl font-bold text-slate-800 capitalize">📖 {title}</h1>
            <Badge variant={statusVariant}>
              {statusLabel}
            </Badge>
          </div>
          <p className="text-slate-500">
            Voice: {voiceName} | Started: {startTimeStr} | Duration: {totalDuration} {isRunning && '(running)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Auto-refresh:</span>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={clsx(
              'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
              autoRefresh ? 'bg-success-100 text-success-700' : 'bg-slate-100 text-slate-600'
            )}
          >
            {autoRefresh ? 'ON' : 'OFF'}
          </button>
        </div>
      </div>

      {/* Job Controls - Pause/Resume/Stop */}
      <Card>
        <CardHeader title="Job Controls" />
        <CardBody className="space-y-4">
          {/* Pause Status Indicator */}
          <PauseStatusIndicator
            status={job.status}
            pausedAtCheckpoint={job.paused_at_checkpoint}
            pausedAt={job.paused_at}
            stoppedAt={job.stopped_at}
            stopReason={job.stop_reason}
          />

          {/* Control Buttons */}
          <JobControlButtons
            jobId={job.job_id}
            status={job.status}
            currentStep={statusData?.current_step || undefined}
            pauseRequested={job.pause_requested}
            onPause={handlePause}
            onResume={handleResume}
            onStop={handleStop}
            onDelete={job.status === 'stopped' ? handleDelete : undefined}
          />

          {/* Checkpoint Preview - show upcoming pause points */}
          {(job.status === 'processing' || job.status === 'running' || job.status === 'pending') && (
            <CheckpointPreview
              currentStep={statusData?.current_step}
              pauseRequested={job.pause_requested}
              completedSteps={
                pipelineProgress?.step_details
                  ? Object.entries(pipelineProgress.step_details)
                      .filter(([, detail]) => detail.completed)
                      .map(([step]) => step)
                  : undefined
              }
            />
          )}
        </CardBody>
      </Card>

      {/* Pipeline Flow Diagram */}
      <Card>
        <CardHeader title="Pipeline Flow" />
        <CardBody>
          <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
            {PIPELINE_STEPS.map((step, index) => {
              const stepData = stepDetails[step.key];
              const duration = calculateDuration(stepData?.startTime, stepData?.endTime);

              return (
                <div key={step.key} className="flex items-center">
                  <div
                    className={clsx(
                      'px-4 py-3 rounded-xl border-2 text-center min-w-[100px]',
                      getStepStatusStyle(stepData)
                    )}
                  >
                    <div className="text-2xl mb-1">{step.icon}</div>
                    <p className="text-xs font-medium">{step.name}</p>
                    <p className="text-xs opacity-75">{formatDuration(duration)}</p>
                  </div>
                  {index < PIPELINE_STEPS.length - 1 && (
                    <div className="w-8 h-0.5 bg-slate-200 mx-1" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="max-w-md mx-auto">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-500">Progress</span>
              <span className="font-medium text-slate-700">Step {currentStep}/{totalSteps}</span>
            </div>
            <Progress value={progress} showLabel variant="primary" size="lg" />
          </div>
        </CardBody>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{pipelineProgress?.completed_steps || 0}</p>
            <p className="text-sm text-slate-500">📄 Steps Completed</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{statusData?.events_count || '--'}</p>
            <p className="text-sm text-slate-500">🔄 Events Emitted</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{ttsBatches.completed}/{ttsBatches.total || '--'}</p>
            <p className="text-sm text-slate-500">🎵 TTS Batches</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-3xl font-bold text-slate-800">{progress}%</p>
            <p className="text-sm text-slate-500">📊 Progress</p>
          </CardBody>
        </Card>
      </div>

      {/* TTS Batch Progress */}
      {ttsBatches.total > 0 && (
        <Card>
          <CardHeader title="TTS Batch Progress" />
          <CardBody>
            <div className="mb-4">
              <div className="flex gap-1">
                {Array.from({ length: ttsBatches.total }).map((_, i) => (
                  <div
                    key={i}
                    className={clsx(
                      'h-4 flex-1 rounded-sm',
                      i < ttsBatches.completed
                        ? 'bg-success-500'
                        : i < ttsBatches.completed + (ttsBatches.inProgress > 0 ? ttsBatches.inProgress : 1)
                        ? 'bg-primary-400 animate-pulse'
                        : 'bg-slate-200'
                    )}
                  />
                ))}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {ttsBatches.completed}/{ttsBatches.total} complete • {ttsBatches.inProgress || 0} active • {ttsBatches.pending} pending
              </p>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Stuck Warning */}
      {showSlowProcessingWarning && (
        <Card className="border-amber-200 bg-amber-50">
          <CardBody>
            <div className="flex items-center gap-3">
              <ExclamationTriangleIcon className="w-6 h-6 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-800">This is taking longer than usual</p>
                <p className="text-sm text-amber-700">
                  {statusData?.stuck_reason || 'No recent activity detected for this job.'}
                  {lastActivityMinutes !== null && ` (last activity ~${lastActivityMinutes} min ago)`}
                </p>
              </div>
            </div>
          </CardBody>
        </Card>
      )}

      {/* Live Event Timeline */}
      <Card>
        <CardHeader
          title="Live Event Timeline"
          action={
            <select
              value={eventFilter}
              onChange={(e) => setEventFilter(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-slate-200 text-sm"
            >
              <option value="all">All Events</option>
              <option value="tts">TTS Events</option>
              <option value="success">Completed Only</option>
            </select>
          }
        />
        <CardBody>
          {filteredEvents.length > 0 ? (
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {filteredEvents.map((event, index) => {
                const isSuccess = event.event_name.includes('_end') || event.event_name.includes('complete');
                return (
                  <div
                    key={index}
                    className="flex items-start gap-3 text-sm"
                  >
                    <span className="text-slate-400 font-mono w-20">{formatTime(event.timestamp)}</span>
                    <span
                      className={clsx(
                        'w-2 h-2 mt-1.5 rounded-full',
                        isSuccess ? 'bg-success-500' : 'bg-primary-400'
                      )}
                    />
                    <div>
                      <span className="font-medium text-slate-700">{event.event_name}</span>
                      {event.data && Object.keys(event.data).length > 0 && (
                        <span className="text-slate-500 ml-2">
                          {JSON.stringify(event.data).slice(0, 50)}
                          {JSON.stringify(event.data).length > 50 && '...'}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-500 text-center py-8">
              {events.length === 0 ? 'No events recorded yet' : 'No events match the current filter'}
            </p>
          )}
        </CardBody>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <Button 
          variant="outline" 
          leftIcon={<ArrowPathIcon className="w-4 h-4" />}
          onClick={fetchData}
        >
          Refresh Now
        </Button>
        <Button 
          variant="outline" 
          leftIcon={<ArrowPathIcon className="w-4 h-4" />}
          onClick={() => setAutoRefresh(!autoRefresh)}
        >
          {autoRefresh ? 'Pause' : 'Resume'} Updates
        </Button>
        <Button variant="outline" leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}>
          Export Events
        </Button>
      </div>

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}
